import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { CreateRestaurantCategoryRequest } from '../../client-sdk';

export const useAddCategory = () => {
  const { addCategory } = useApi();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryData: CreateRestaurantCategoryRequest) =>
      addCategory(categoryData),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Category created successfully',
        'success'
      );

      queryClient.invalidateQueries({ queryKey: ['categories'] });
      // showMessage("Category added successfully!", "success");
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to create Category',
        'error'
      );
    },
  });
};

export const useEditStoreCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editStoreCategoryId } = useApi();

  return useMutation({
    mutationKey: ['editStoreCategory'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editStoreCategoryId(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store Category Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['categories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      // Try multiple error message paths
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail?.[0]?.message ||
        error.response?.data?.detail?.[0]?.type ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update the Store Category';
      
      showMessage(errorMessage, 'error');
    },
  });
};

type Params = {
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
};

// List Store Category
export const useGetListCategories = (queryParams: Params) => {

  const { getListCategories } = useApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['categories', queryParams],
    queryFn: () => {
      return getListCategories(queryParams);
    },
  });
  const isLoading = isFetching || isRefetching;


  return {
    data,
    isLoading,
    refetch
  };
};

//Delete Store Category
export const useDeleteStoreCategoryById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { deleteStoreCategoryById } = useApi();

  return useMutation({
    mutationKey: ['DeleteStoreCategory'],
    mutationFn: (id: string) => deleteStoreCategoryById(id),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'SubAdmin deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['categories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to deleted SubAdmin',
        'error'
      );
    },
  });
};

// Block and unblock Category
export const useBlockOrUnblockStoreCategoryById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockOrUnblockStoreCategoryById } = useApi();

  return useMutation({
    mutationKey: ['BlockOrUnblockStoreCategory'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockOrUnblockStoreCategoryById({ id, status: data.status }),
    onSuccess: () => {
      showMessage('Store Category blocked successfully', 'success');
      queryClient.invalidateQueries({
        queryKey: ['categories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to block the Store Category',
        'error'
      );
    },
  });
}; 